import Uploading from "components/Uploading";
import { WISDOM_PMS_ROLE_PERMISSION } from "constants/constants";
import React, { useEffect, useMemo, useState } from "react";
import { fetchWisdomCurriculumsDashboard } from "services/wisdom/wisdomData";
import { roundNumber } from "utils/roundNumber";

const WEAK_THRESHOLD = 70;
const SOURCE_COLORS = ["#2E93CC", "#E0A32B", "#8B7CE8", "#E0704F", "#22B07D", "#D8543F", "#5FE39D", "#B09509"];

const scoreColor = (v) => v >= 90 ? "#22B07D" : v >= 80 ? "#2E93CC" : v >= 70 ? "#E0A32B" : "#D8543F";
const fmt = (v) => v == null ? "—" : `${roundNumber(v)}%`;
const avg = (bucket) => (bucket && bucket.count) ? bucket.sum / bucket.count : null;

const curriculumName = (curriculum) => curriculum?.code || curriculum?.name || "بدون اسم";
const employeeFullName = (employee) =>
    [employee?.first_name, employee?.middle_name, employee?.last_name].filter(Boolean).join(" ") || "بدون اسم";

const CurriculumDashboard = ({ orgId, year, month }) => {
    const [fetching, setFetching] = useState(true);
    const [dashboard, setDashboard] = useState([]);
    const [currentSource, setCurrentSource] = useState(null);
    const [selectedCurriculum, setSelectedCurriculum] = useState(null);
    const [selectedSource, setSelectedSource] = useState(null);

    useEffect(() => {
        const load = async () => {
            try {
                const response = await fetchWisdomCurriculumsDashboard(orgId, year, month);
                setDashboard(response || []);
                setSelectedSource(null);
            } catch (err) {
                console.error("error:", err);
            } finally {
                setFetching(false);
            }
        };

        load();
    }, [orgId, year, month]);

    const data = useMemo(() => {
        const curriculums = {};
        const sources = {};
        const domains = {};
        const bySourceCount = {};
        const bySourceReport = {};
        const byDomainAll = {};
        const bySourceDomain = {};
        const byCurriculumSource = {};
        const byCurriculumSourceDomain = {};
        const bySourceForms = {};
        const domainSourcesSeen = {};

        dashboard.forEach((report) => {
            const results = report.results || [];
            if (!results.length) return;

            const form = results[0]?.questionResult?.sub_field?.field?.form;
            const formType = (form?.code || "").split(" | ")[1];
            if (formType && formType !== "C") return;
            const sourceKey = (form?.code || "").split(" | ")[0] || "?";

            if (!sources[sourceKey]) {
                sources[sourceKey] = {
                    key: sourceKey,
                    label: WISDOM_PMS_ROLE_PERMISSION[sourceKey] || sourceKey,
                    color: SOURCE_COLORS[Object.keys(sources).length % SOURCE_COLORS.length],
                };
            }

            const curriculum = report.curriculum || report.assessee?.curriculum;
            const curriculumId = curriculum?.id;
            if (!curriculumId) return;

            if (!curriculums[curriculumId]) {
                curriculums[curriculumId] = {
                    id: curriculumId,
                    name: curriculumName(curriculum),
                };
            }

            bySourceCount[sourceKey] = (bySourceCount[sourceKey] || 0) + 1;

            bySourceReport[sourceKey] ??= { sum: 0, count: 0 };
            bySourceReport[sourceKey].sum += Number(report.avg_score) || 0;
            bySourceReport[sourceKey].count += 1;

            byCurriculumSource[curriculumId] ??= {};
            byCurriculumSource[curriculumId][sourceKey] ??= { sum: 0, count: 0 };
            byCurriculumSource[curriculumId][sourceKey].sum += Number(report.avg_score) || 0;
            byCurriculumSource[curriculumId][sourceKey].count += 1;

            bySourceForms[sourceKey] ??= [];
            bySourceForms[sourceKey].push({
                id: report.id ?? `${sourceKey}-${bySourceForms[sourceKey].length}`,
                name: employeeFullName(report.assessor?.employee ? report.assessor?.employee : report.assessor?.student),
                curriculumName: curriculumName(curriculum),
                score: Number(report.avg_score) || 0,
                date: report.createdAt || report.created_at || null,
            });

            const domainSums = {};
            results.forEach((r) => {
                const field = r.questionResult?.sub_field?.field;
                if (!field || r.score == null || !r.questionResult?.max_score) return;

                const domainKey = field.ar_name || field.en_name || `field-${field.id}`;
                domains[domainKey] ??= { key: domainKey, label: domainKey };
                domainSums[domainKey] ??= { sum: 0, count: 0 };
                domainSums[domainKey].sum += (Number(r.score) / Number(r.questionResult.max_score)) * 100;
                domainSums[domainKey].count += 1;
            });

            Object.entries(domainSums).forEach(([domainKey, bucket]) => {
                const domainScore = bucket.sum / bucket.count;

                bySourceDomain[sourceKey] ??= {};
                bySourceDomain[sourceKey][domainKey] ??= { sum: 0, count: 0 };
                bySourceDomain[sourceKey][domainKey].sum += domainScore;
                bySourceDomain[sourceKey][domainKey].count += 1;

                byDomainAll[domainKey] ??= { sum: 0, count: 0 };
                byDomainAll[domainKey].sum += domainScore;
                byDomainAll[domainKey].count += 1;

                byCurriculumSourceDomain[curriculumId] ??= {};
                byCurriculumSourceDomain[curriculumId][sourceKey] ??= {};
                byCurriculumSourceDomain[curriculumId][sourceKey][domainKey] ??= { sum: 0, count: 0 };
                byCurriculumSourceDomain[curriculumId][sourceKey][domainKey].sum += domainScore;
                byCurriculumSourceDomain[curriculumId][sourceKey][domainKey].count += 1;

                domainSourcesSeen[domainKey] ??= new Set();
                domainSourcesSeen[domainKey].add(sourceKey);
            });
        });

        const sourceList = Object.values(sources);
        const curriculumList = Object.values(curriculums);

        const domainAvg = {};
        Object.keys(domains).forEach((k) => { domainAvg[k] = avg(byDomainAll[k]); });

        const domainList = Object.values(domains).sort(
            (a, b) => (domainAvg[a.key] ?? 100) - (domainAvg[b.key] ?? 100)
        );

        const sourceAvg = {};
        sourceList.forEach((s) => { sourceAvg[s.key] = avg(bySourceReport[s.key]); });

        const totalReports = dashboard.length;
        const overall = avg({ sum: dashboard.reduce((s, r) => s + (Number(r.avg_score) || 0), 0), count: totalReports });

        const curriculumWeak = {};
        let totalFlags = 0;
        curriculumList.forEach((c) => {
            const weak = [];
            const bySrc = byCurriculumSourceDomain[c.id] || {};

            Object.entries(bySrc).forEach(([sourceKey, byDom]) => {
                Object.entries(byDom).forEach(([domainKey, bucket]) => {
                    const v = avg(bucket);
                    if (v != null && v < WEAK_THRESHOLD) {
                        weak.push({ source: sourceKey, domain: domainKey, value: v });
                        totalFlags++;
                    }
                });
            });

            curriculumWeak[c.id] = weak;
        });

        const uncoveredDomains = domainList.filter((d) => {
            const seen = domainSourcesSeen[d.key] || new Set();
            return [...seen].filter((k) => k !== "Self").length === 0;
        });

        return {
            sources: sourceList,
            domains: domainList,
            curriculums: curriculumList,
            bySourceCount,
            sourceAvg,
            domainAvg,
            bySourceDomain,
            byCurriculumSource,
            byCurriculumSourceDomain,
            bySourceForms,
            curriculumWeak,
            totalReports,
            overall,
            totalFlags,
            uncoveredDomains,
            worstDomain: domainList[0] || null,
        };
    }, [dashboard]);

    if (fetching) return <Uploading />;

    if (!data.totalReports) {
        return (
            <div dir="rtl" className="w-[min(92vw,700px)] p-8 text-center text-[#9DB2CE]">
                لا توجد بيانات تقييم متاحة لهذه الجهة بعد.
            </div>
        );
    }

    const activeSource = currentSource ?? data.sources[0]?.key;
    const activeSourceLabel = data.sources.find((s) => s.key === activeSource)?.label || "";

    const rankedCurriculums = data.curriculums
        .filter((c) => data.byCurriculumSource[c.id]?.[activeSource])
        .map((c) => ({ ...c, value: avg(data.byCurriculumSource[c.id][activeSource]) }))
        .sort((a, b) => b.value - a.value);

    let ringOffset = 0;

    const selected = selectedCurriculum ? data.curriculums.find((c) => c.id === selectedCurriculum) : null;
    const selectedWeak = selected ? data.curriculumWeak[selected.id] || [] : [];
    const selectedWeakByDomain = selectedWeak.reduce((acc, w) => {
        (acc[w.domain] ??= []).push(w);
        return acc;
    }, {});

    const selectedSourceObj = selectedSource ? data.sources.find((s) => s.key === selectedSource) : null;
    const selectedForms = selectedSourceObj
        ? (data.bySourceForms[selectedSourceObj.key] || []).slice().sort((a, b) => b.score - a.score)
        : [];

    return (
        <div dir="rtl" className="w-[min(94vw,1080px)] text-[#EAF1FA]">
            <div className="mb-1">
                <h1 className="text-lg font-bold">تحليل تقييم المناهج</h1>
                <p className="text-xs text-[#6C82A3] mt-0.5">
                    {data.sources.length} مصادر تقييم · {data.domains.length} مجالات · {data.curriculums.length} منهج
                </p>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 my-3">
                <div className="rounded-lg border border-[#26385A] bg-[#1C2C47] p-3 text-center">
                    <span className="block text-[10px] text-[#6C82A3]">التقييمات</span>
                    <b className="block text-xl font-semibold mt-1">{data.totalReports}</b>
                </div>
                <div className="rounded-lg border border-[#26385A] bg-[#1C2C47] p-3 text-center">
                    <span className="block text-[10px] text-[#6C82A3]">المناهج</span>
                    <b className="block text-xl font-semibold mt-1">{data.curriculums.length}</b>
                </div>
                <div className="rounded-lg border border-[#26385A] bg-[#1C2C47] p-3 text-center">
                    <span className="block text-[10px] text-[#6C82A3]">المتوسط العام</span>
                    <b className="block text-xl font-semibold mt-1">{fmt(data.overall)}</b>
                </div>
                <div className="rounded-lg border border-[#26385A] bg-[#1C2C47] p-3 text-center">
                    <span className="block text-[10px] text-[#6C82A3]">نقاط الضعف</span>
                    <b className="block text-xl font-semibold mt-1" style={{ color: "#D8543F" }}>{data.totalFlags}</b>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Donut */}
                <section className="rounded-xl border border-[#26385A] bg-[#1C2C47] p-3">
                    <h2 className="text-[13px] font-semibold text-[#9DB2CE] mb-2">نسبة كل مصدر</h2>
                    <div className="flex flex-col items-center gap-3">
                        <svg width="112" height="112" viewBox="0 0 112 112">
                            <g transform="translate(56,56) rotate(-90)">
                                {data.sources.map((s) => {
                                    const count = data.bySourceCount[s.key] || 0;
                                    const C = 2 * Math.PI * 42;
                                    const len = (count / data.totalReports) * C;
                                    const el = (
                                        <circle
                                            key={s.key}
                                            r="42"
                                            fill="none"
                                            stroke={s.color}
                                            strokeWidth="15"
                                            strokeDasharray={`${Math.max(len - 2, 0)} ${C - len + 2}`}
                                            strokeDashoffset={-ringOffset}
                                        />
                                    );
                                    ringOffset += len;
                                    return el;
                                })}
                            </g>
                            <text x="56" y="54" textAnchor="middle" fill="#EAF1FA" fontSize="18" fontWeight="600">
                                {data.totalReports}
                            </text>
                            <text x="56" y="67" textAnchor="middle" fill="#6C82A3" fontSize="8">تقييم</text>
                        </svg>
                        <div className="w-full flex flex-col gap-1">
                            {data.sources.map((s) => (
                                <div key={s.key} className="grid grid-cols-[8px_1fr_auto] items-center gap-2 text-[11px]">
                                    <span className="w-2 h-2 rounded-sm" style={{ background: s.color }} />
                                    <span className="text-[#9DB2CE] truncate">{s.label}</span>
                                    <span className="font-semibold">
                                        {roundNumber(((data.bySourceCount[s.key] || 0) / data.totalReports) * 100)}%
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Source averages */}
                <section className="rounded-xl border border-[#26385A] bg-[#1C2C47] p-3">
                    <h2 className="text-[13px] font-semibold text-[#9DB2CE] mb-2">
                        متوسط تقييم المناهج حسب المصدر <em className="text-[10px] text-[#6C82A3] font-normal">وعدد التقييمات — اضغط لعرض النماذج</em>
                    </h2>
                    <div className="flex flex-col gap-2">
                        {data.sources.slice().sort((a, b) => (data.sourceAvg[b.key] ?? 0) - (data.sourceAvg[a.key] ?? 0)).map((s) => {
                            const isSel = selectedSource === s.key;
                            return (
                                <div
                                    key={s.key}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => setSelectedSource(isSel ? null : s.key)}
                                    className={`cursor-pointer rounded-md p-1 -m-1 border ${isSel ? "bg-[#26385A] border-[#33507E]" : "border-transparent hover:bg-[#26385A]/60"}`}
                                >
                                    <div className="flex items-baseline gap-1 text-[11px] mb-1">
                                        <b className="font-medium">{s.label}</b>
                                        <span className="text-[#6C82A3] text-[10px]">{data.bySourceCount[s.key] || 0} تقييم</span>
                                        <span className="mr-auto font-semibold" style={{ color: s.color }}>{fmt(data.sourceAvg[s.key])}</span>
                                    </div>
                                    <div className="h-[9px] rounded-sm bg-[#26385A] overflow-hidden">
                                        <div
                                            className="h-full rounded-sm"
                                            style={{ width: `${data.sourceAvg[s.key] || 0}%`, background: s.color }}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>
            </div>

            {/* Domain x Source matrix */}
            <section className="rounded-xl border border-[#26385A] bg-[#1C2C47] p-3 mt-3">
                <h2 className="text-[13px] font-semibold text-[#9DB2CE] mb-2">
                    نقاط الضعف حسب مجالات المنهج <em className="text-[10px] text-[#6C82A3] font-normal">لكل مصدر مجالاته فقط</em>
                </h2>

                {data.worstDomain && (
                    <div className="rounded-lg border border-[#D8543F]/40 bg-[#D8543F]/10 text-[#F0B4AA] text-[11px] leading-relaxed p-2 mb-2">
                        <b className="block text-[#FFD3CA] font-semibold mb-0.5">
                            أضعف مجال: {data.worstDomain.label} بمتوسط {fmt(data.domainAvg[data.worstDomain.key])}
                        </b>
                        تم رصد {data.totalFlags} نقطة ضعف (أقل من {WEAK_THRESHOLD}%) عبر المناهج والمجالات.
                    </div>
                )}

                {data.uncoveredDomains.length > 0 && (
                    <div className="rounded-lg border border-[#E0A32B]/40 bg-[#E0A32B]/10 text-[#EFCE94] text-[11px] leading-relaxed p-2 mb-2">
                        <b className="block text-[#FBE6BC] font-semibold mb-0.5">
                            {data.uncoveredDomains.length} مجال بلا مقيّم خارجي
                        </b>
                        {data.uncoveredDomains.map((d) => d.label).join("، ")} — لا يقيّمه سوى واضع المنهج نفسه.
                    </div>
                )}

                <div className="overflow-x-auto">
                    <table className="w-full border-separate" style={{ borderSpacing: 3 }}>
                        <thead>
                            <tr>
                                <th className="text-[9px] font-medium text-[#6C82A3] text-right w-32">المجال</th>
                                {data.sources.map((s) => (
                                    <th key={s.key} className="text-[9px] font-medium text-[#9DB2CE] w-12">{s.label}</th>
                                ))}
                                <th className="text-[9px] font-medium text-[#9DB2CE] w-12">المتوسط</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.domains.map((d) => (
                                <tr key={d.key}>
                                    <td className="text-[10px] text-right pe-1 truncate max-w-[130px]">{d.label}</td>
                                    {data.sources.map((s) => {
                                        const v = avg(data.bySourceDomain[s.key]?.[d.key]);
                                        return (
                                            <td key={s.key}>
                                                {v == null ? (
                                                    <div className="h-7 rounded-md flex items-center justify-center text-[#4E627E] bg-white/[0.02]">—</div>
                                                ) : (
                                                    <div
                                                        className="h-7 rounded-md flex items-center justify-center text-[11px] font-semibold"
                                                        style={{
                                                            background: `${scoreColor(v)}22`,
                                                            color: scoreColor(v),
                                                            boxShadow: v < WEAK_THRESHOLD ? `inset 0 0 0 1.5px ${scoreColor(v)}` : "none",
                                                        }}
                                                    >
                                                        {roundNumber(v)}%
                                                    </div>
                                                )}
                                            </td>
                                        );
                                    })}
                                    <td>
                                        <div
                                            className="h-7 rounded-md flex items-center justify-center text-[11px] font-semibold bg-[#26385A] border border-[#33507E]"
                                            style={{ color: scoreColor(data.domainAvg[d.key]) }}
                                        >
                                            {fmt(data.domainAvg[d.key])}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="flex flex-wrap gap-3 text-[10px] text-[#9DB2CE] mt-2">
                    <span className="flex items-center gap-1"><b className="w-[7px] h-[7px] rounded-sm block" style={{ background: "#22B07D" }} />٩٠+</span>
                    <span className="flex items-center gap-1"><b className="w-[7px] h-[7px] rounded-sm block" style={{ background: "#2E93CC" }} />٨٠–٨٩</span>
                    <span className="flex items-center gap-1"><b className="w-[7px] h-[7px] rounded-sm block" style={{ background: "#E0A32B" }} />٧٠–٧٩</span>
                    <span className="flex items-center gap-1"><b className="w-[7px] h-[7px] rounded-sm block" style={{ background: "#D8543F" }} />أقل من {WEAK_THRESHOLD} · نقطة ضعف</span>
                    <span className="text-[#6C82A3]">— = المجال غير مشمول لهذا المصدر</span>
                </div>
            </section>

            {/* Curriculum ranking */}
            <section className="rounded-xl border border-[#26385A] bg-[#1C2C47] p-3 mt-3">
                <h2 className="text-[13px] font-semibold text-[#9DB2CE] mb-2">
                    ترتيب المناهج <em className="text-[10px] text-[#6C82A3] font-normal">النقطة الحمراء = به نقطة ضعف · اضغط لعرض مجالاته</em>
                </h2>

                <div className="flex gap-1 flex-wrap mb-2">
                    {data.sources.map((s) => {
                        const on = s.key === activeSource;
                        return (
                            <button
                                key={s.key}
                                onClick={() => setCurrentSource(s.key)}
                                className="rounded-full border px-2.5 py-1 text-[10px] flex items-center gap-1.5 whitespace-nowrap"
                                style={on
                                    ? { background: s.color, borderColor: s.color, color: "#fff" }
                                    : { background: "#26385A", borderColor: "#26385A", color: "#9DB2CE" }}
                            >
                                <span className="w-1.5 h-1.5 rounded-full" style={{ background: on ? "#fff" : s.color }} />
                                {s.label}
                                <span className="text-[9px]" style={{ color: on ? "rgba(255,255,255,.75)" : "#6C82A3" }}>
                                    {data.bySourceCount[s.key] || 0}
                                </span>
                            </button>
                        );
                    })}
                </div>

                <div className="max-h-[220px] overflow-y-auto pe-1">
                    {rankedCurriculums.map((c) => {
                        const weakCount = (data.curriculumWeak[c.id] || []).length;
                        const isSel = selectedCurriculum === c.id;
                        return (
                            <div
                                key={c.id}
                                role="button"
                                tabIndex={0}
                                onClick={() => setSelectedCurriculum(isSel ? null : c.id)}
                                className={`grid grid-cols-[120px_1fr_50px] items-center gap-2 px-1 py-1 rounded-md cursor-pointer border ${isSel ? "bg-[#26385A] border-[#33507E]" : "border-transparent hover:bg-[#26385A]/60"}`}
                            >
                                <span className="text-[11px] text-[#9DB2CE] truncate">{c.name}</span>
                                <span className="h-[10px] rounded-sm bg-[#26385A] relative overflow-hidden">
                                    <i
                                        className="block h-full rounded-sm"
                                        style={{ width: `${c.value}%`, background: scoreColor(c.value) }}
                                    />
                                    <i
                                        className="absolute top-0 bottom-0 w-px bg-[#6C82A3] opacity-60"
                                        style={{ right: `${data.sourceAvg[activeSource] || 0}%` }}
                                    />
                                </span>
                                <span className="text-[11px] font-semibold flex items-center justify-end gap-1" style={{ color: scoreColor(c.value) }}>
                                    {weakCount > 0 && <span className="w-[5px] h-[5px] rounded-full bg-[#D8543F]" title={`${weakCount} نقطة ضعف`} />}
                                    {roundNumber(c.value)}%
                                </span>
                            </div>
                        );
                    })}
                    {!rankedCurriculums.length && (
                        <p className="text-[11px] text-[#6C82A3] text-center py-4">لا توجد مناهج مقيّمة من {activeSourceLabel}.</p>
                    )}
                </div>

                {selected && (
                    <div className="rounded-lg border border-[#33507E] bg-[#16233A] p-3 mt-3">
                        <div className="text-[13px] font-semibold mb-2">
                            {selected.name}
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full border-separate" style={{ borderSpacing: 3 }}>
                                <thead>
                                    <tr>
                                        <th className="text-[9px] font-medium text-[#6C82A3] text-right w-32">المجال</th>
                                        {data.sources.map((s) => (
                                            <th key={s.key} className="text-[9px] font-medium text-[#9DB2CE] w-12">{s.label}</th>
                                        ))}
                                        <th className="text-[9px] font-medium text-[#9DB2CE] w-12">المتوسط</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.domains.map((d) => {
                                        const rowValues = data.sources
                                            .map((s) => avg(data.byCurriculumSourceDomain[selected.id]?.[s.key]?.[d.key]))
                                            .filter((v) => v != null);
                                        const rowAvg = rowValues.length ? rowValues.reduce((a, b) => a + b, 0) / rowValues.length : null;

                                        return (
                                            <tr key={d.key}>
                                                <td className="text-[10px] text-right pe-1 truncate max-w-[130px]">{d.label}</td>
                                                {data.sources.map((s) => {
                                                    const v = avg(data.byCurriculumSourceDomain[selected.id]?.[s.key]?.[d.key]);
                                                    return (
                                                        <td key={s.key}>
                                                            {v == null ? (
                                                                <div className="h-7 rounded-md flex items-center justify-center text-[#4E627E] bg-white/[0.02]">—</div>
                                                            ) : (
                                                                <div
                                                                    className="h-7 rounded-md flex items-center justify-center text-[11px] font-semibold"
                                                                    style={{
                                                                        background: `${scoreColor(v)}22`,
                                                                        color: scoreColor(v),
                                                                        boxShadow: v < WEAK_THRESHOLD ? `inset 0 0 0 1.5px ${scoreColor(v)}` : "none",
                                                                    }}
                                                                >
                                                                    {roundNumber(v)}%
                                                                </div>
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                                <td>
                                                    <div
                                                        className="h-7 rounded-md flex items-center justify-center text-[11px] font-semibold bg-[#26385A] border border-[#33507E]"
                                                        style={{ color: scoreColor(rowAvg) }}
                                                    >
                                                        {fmt(rowAvg)}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {selectedWeak.length ? (
                            <div className="rounded-md border border-[#D8543F]/30 bg-[#D8543F]/10 text-[#F0B4AA] text-[10px] leading-relaxed p-2 mt-2">
                                <b className="block text-[#FFD3CA] text-[11px] mb-1">نقاط الضعف: {selectedWeak.length}</b>
                                {Object.entries(selectedWeakByDomain).map(([domainKey, items]) => {
                                    const domainLabel = data.domains.find((d) => d.key === domainKey)?.label || domainKey;
                                    return (
                                        <div key={domainKey}>
                                            {domainLabel} — {items.map((it) => `${data.sources.find((s) => s.key === it.source)?.label || it.source} ${roundNumber(it.value)}%`).join("، ")}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="rounded-md border border-[#22B07D]/30 bg-[#22B07D]/10 text-[#9FE0C6] text-[10px] leading-relaxed p-2 mt-2">
                                <b className="block text-[#C6F0DF] text-[11px] mb-1">لا نقاط ضعف</b>
                                في كل المجالات المقيّمة عند {WEAK_THRESHOLD}% أو أعلى.
                            </div>
                        )}
                    </div>
                )}
            </section>

            {/* Forms for the selected source */}
            {selectedSourceObj && (
                <section className="rounded-xl border border-[#26385A] bg-[#1C2C47] p-3 mt-3">
                    <div className="flex items-center gap-2 mb-2">
                        <span className="w-2 h-2 rounded-sm" style={{ background: selectedSourceObj.color }} />
                        <h2 className="text-[13px] font-semibold text-[#9DB2CE]">
                            نماذج {selectedSourceObj.label}
                            <em className="text-[10px] text-[#6C82A3] font-normal ms-1">({selectedForms.length} نموذج)</em>
                        </h2>
                        <button
                            type="button"
                            onClick={() => setSelectedSource(null)}
                            className="mr-auto text-[10px] text-[#6C82A3] hover:text-[#EAF1FA]"
                        >
                            إغلاق ✕
                        </button>
                    </div>

                    <div className="flex flex-col gap-1 max-h-[300px] overflow-y-auto pe-1">
                        {selectedForms.map((f) => {
                            const d = f.date ? new Date(f.date) : null;
                            const dateLabel = d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString("ar-EG") : "—";
                            return (
                                <div
                                    key={f.id}
                                    className="grid grid-cols-[1fr_1fr_auto_56px] items-center gap-2 rounded-md border border-[#26385A] bg-[#16233A] px-2 py-1.5"
                                >
                                    <span className="text-[11px] text-[#EAF1FA] truncate">{f.name}</span>
                                    <span className="text-[10px] text-[#9DB2CE] truncate" title={f.curriculumName}>{f.curriculumName}</span>
                                    <span className="text-[10px] text-[#6C82A3]">{dateLabel}</span>
                                    <span
                                        className="text-[11px] font-semibold text-center rounded-md py-0.5"
                                        style={{ background: `${scoreColor(f.score)}22`, color: scoreColor(f.score) }}
                                    >
                                        {roundNumber(f.score)}%
                                    </span>
                                </div>
                            );
                        })}
                        {!selectedForms.length && (
                            <p className="text-[11px] text-[#6C82A3] text-center py-4">لا توجد نماذج لهذا المصدر.</p>
                        )}
                    </div>
                </section>
            )}
        </div>
    );
};

export default CurriculumDashboard;
